import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/ui/CurrencyInput";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { buildPlannedRepaymentSchedule as buildUS } from "@/lib/usaLoanEngine";
import { buildPlannedRepaymentSchedule as buildCA, type PaymentFrequency } from "@/lib/calculationEngine";
import {
  reprojectFromActuals,
  buildAsTrackedSchedule,
  type Reprojection,
  type ActualPaymentRecord,
} from "@/lib/reprojection";
import type { ScenarioRow } from "@/lib/scenarios";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";

type ActualStatus =
  | "paid_as_planned"
  | "paid_different"
  | "skipped"
  | "paid_late"
  | "partial";

const STATUS_LABEL: Record<ActualStatus, string> = {
  paid_as_planned: "Paid as planned",
  paid_different: "Paid different amount",
  skipped: "Skipped / missed",
  paid_late: "Paid late",
  partial: "Partial",
};

const STATUS_BADGE: Record<ActualStatus, string> = {
  paid_as_planned: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  paid_different: "bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30",
  skipped: "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30",
  paid_late: "bg-orange-500/15 text-orange-700 dark:text-orange-300 border-orange-500/30",
  partial: "bg-yellow-500/15 text-yellow-700 dark:text-yellow-300 border-yellow-500/30",
};

type FeeTreatment = "paid_with_payment" | "capitalized";

type ActualRow = {
  payment_number: number;
  status: ActualStatus;
  actual_amount: number | null;
  actual_date: string | null;
  note: string | null;
  payment_method: string | null;
  payment_reference: string | null;
  late_fee: number;
  nsf_fee: number;
  fee_treatment: FeeTreatment;
};

const PAYMENT_METHODS = ["Cheque", "EFT", "E-Transfer", "Wire", "Auto-Debit", "Cash", "Other"];

/** Late-charge rule, stored on the scenario so it travels with the loan. */
export type LateFeeRule = {
  enabled: boolean;
  graceDays: number;
  percent: number; // e.g. 10 = 10% of the overdue payment
  minimum: number; // e.g. 50 = $50.00
  capitalizeByDefault: boolean;
};

const DEFAULT_LATE_FEE_RULE: LateFeeRule = {
  enabled: false,
  graceDays: 5,
  percent: 10,
  minimum: 50,
  capitalizeByDefault: false,
};

function daysBetween(fromISO: string, toISO: string): number {
  const a = Date.parse(fromISO);
  const b = Date.parse(toISO);
  if (isNaN(a) || isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000);
}

/** Greater of percent-of-payment or the flat minimum, per the rule. */
export function computeLateFee(rule: LateFeeRule, plannedAmount: number, daysLate: number): number {
  if (!rule.enabled) return 0;
  if (daysLate <= rule.graceDays) return 0;
  const pct = (plannedAmount * (rule.percent || 0)) / 100;
  return Math.round(Math.max(pct, rule.minimum || 0) * 100) / 100;
}

type PlannedRow = {
  paymentNumber: number;
  date: string;
  amount: number;
};

function fmtMoney(n: number | null | undefined) {
  if (n == null || isNaN(n)) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 2 });
}

type PlanInfo = {
  rows: PlannedRow[];
  borrowedAmount: number;
  annualRate: number;
  paymentFrequency: PaymentFrequency;
};

const EMPTY_PLAN: PlanInfo = { rows: [], borrowedAmount: 0, annualRate: 0, paymentFrequency: "monthly" };

function buildPlanned(scenario: ScenarioRow): PlanInfo {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const data: any = scenario.data ?? {};
  const jurisdiction: "usa" | "canada" = data.jurisdiction === "canada" ? "canada" : "usa";
  const setup = data.setup ?? {};
  const loanAmount = Number(setup.loanAmount) || 0;
  if (loanAmount <= 0) return EMPTY_PLAN;
  const consolidate = !!(setup.hasExistingBalance && setup.consolidateExisting);
  const borrowed = loanAmount + (consolidate ? Number(setup.existingBalance || 0) : 0);
  const rate = Number(setup.disciplineRate ?? setup.interestRate ?? 0) / 100;
  const termCount = Number(setup.numberOfPayments) || 0;
  if (termCount <= 0 || rate <= 0) return EMPTY_PLAN;
  const frequency: PaymentFrequency = setup.paymentFrequency || "monthly";
  const override =
    setup.repaymentMode === "payment" && Number(setup.desiredPayment) > 0
      ? Number(setup.desiredPayment)
      : undefined;
  const input = {
    borrowedAmount: borrowed,
    targetAnnualRate: rate,
    termCount,
    paymentFrequency: frequency,
    startDate: setup.startDate || new Date().toISOString().slice(0, 10),
    overridePaymentAmount: override,
  };
  try {
    const list = jurisdiction === "canada" ? buildCA(input) : buildUS(input);
    return {
      rows: list.map((p, i) => ({ paymentNumber: i + 1, date: p.date, amount: p.amount })),
      borrowedAmount: borrowed,
      annualRate: rate,
      paymentFrequency: frequency,
    };
  } catch (e) {
    console.warn("Failed to build planned schedule", e);
    return EMPTY_PLAN;
  }
}

export function ActualPaymentTracking({
  scenario,
  userId,
}: {
  scenario: ScenarioRow;
  userId: string;
}) {
  const planInfo = useMemo(() => buildPlanned(scenario), [scenario]);
  const planned = planInfo.rows;
  const [actuals, setActuals] = useState<Record<number, ActualRow>>({});
  const [loading, setLoading] = useState(true);
  const [showAsTracked, setShowAsTracked] = useState(false);
  const [rule, setRule] = useState<LateFeeRule>(() => {
    const data = (scenario.data ?? {}) as Record<string, unknown>;
    const stored = data.lateFeeRule as Partial<LateFeeRule> | undefined;
    return { ...DEFAULT_LATE_FEE_RULE, ...(stored ?? {}) };
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("scenario_payment_actuals")
        .select(
          "payment_number,status,actual_amount,actual_date,note,payment_method,payment_reference,late_fee,nsf_fee,fee_treatment"
        )
        .eq("scenario_id", scenario.id);
      if (cancelled) return;
      if (error) {
        toast.error(error.message);
      } else {
        const map: Record<number, ActualRow> = {};
        (data ?? []).forEach((r) => {
          map[r.payment_number] = {
            payment_number: r.payment_number,
            status: r.status as ActualStatus,
            actual_amount: r.actual_amount as number | null,
            actual_date: r.actual_date as string | null,
            note: r.note as string | null,
            payment_method: (r.payment_method as string | null) ?? null,
            payment_reference: (r.payment_reference as string | null) ?? null,
            late_fee: Number(r.late_fee ?? 0),
            nsf_fee: Number(r.nsf_fee ?? 0),
            fee_treatment: ((r.fee_treatment as FeeTreatment | null) ?? "paid_with_payment"),
          };
        });
        setActuals(map);
      }
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [scenario.id]);

  /** Persist the late-charge rule alongside the loan it governs. */
  async function saveRule(next: LateFeeRule) {
    setRule(next);
    const data = { ...((scenario.data ?? {}) as Record<string, unknown>), lateFeeRule: next };
    const { error } = await supabase
      .from("scenarios")
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .update({ data: data as any })
      .eq("id", scenario.id);
    if (error) toast.error(error.message);
  }

  async function upsertRow(p: PlannedRow, patch: Partial<ActualRow> & { status: ActualStatus }) {
    const prevRow = actuals[p.paymentNumber];
    const next: ActualRow = {
      payment_number: p.paymentNumber,
      status: patch.status,
      actual_amount: patch.actual_amount ?? prevRow?.actual_amount ?? null,
      actual_date: patch.actual_date ?? prevRow?.actual_date ?? null,
      note: patch.note ?? prevRow?.note ?? null,
      payment_method: patch.payment_method ?? prevRow?.payment_method ?? null,
      payment_reference: patch.payment_reference ?? prevRow?.payment_reference ?? null,
      late_fee: patch.late_fee ?? prevRow?.late_fee ?? 0,
      nsf_fee: patch.nsf_fee ?? prevRow?.nsf_fee ?? 0,
      fee_treatment:
        patch.fee_treatment ??
        prevRow?.fee_treatment ??
        (rule.capitalizeByDefault ? "capitalized" : "paid_with_payment"),
    };

    // Rule-driven late charge: applied only when the rule is on, the payment
    // is past its grace period, and no charge has been entered by hand.
    if (
      rule.enabled &&
      patch.late_fee === undefined &&
      (prevRow?.late_fee ?? 0) === 0 &&
      next.actual_date
    ) {
      const suggested = computeLateFee(rule, p.amount, daysBetween(p.date, next.actual_date));
      if (suggested > 0) next.late_fee = suggested;
    }

    // optimistic
    setActuals((prev) => ({ ...prev, [p.paymentNumber]: next }));
    const { error } = await supabase
      .from("scenario_payment_actuals")
      .upsert(
        {
          scenario_id: scenario.id,
          user_id: userId,
          payment_number: p.paymentNumber,
          planned_date: p.date,
          planned_amount: p.amount,
          status: next.status,
          actual_amount: next.actual_amount,
          actual_date: next.actual_date,
          note: next.note,
          payment_method: next.payment_method,
          payment_reference: next.payment_reference,
          late_fee: next.late_fee,
          nsf_fee: next.nsf_fee,
          fee_treatment: next.fee_treatment,
        },
        { onConflict: "scenario_id,payment_number" }
      );
    if (error) {
      toast.error(error.message);
    }
  }

  async function clearRow(p: PlannedRow) {
    setActuals((prev) => {
      const next = { ...prev };
      delete next[p.paymentNumber];
      return next;
    });
    const { error } = await supabase
      .from("scenario_payment_actuals")
      .delete()
      .eq("scenario_id", scenario.id)
      .eq("payment_number", p.paymentNumber);
    if (error) toast.error(error.message);
  }

  // ----- Summary -----
  const today = new Date().toISOString().slice(0, 10);
  const actualRecords: ActualPaymentRecord[] = useMemo(
    () =>
      Object.values(actuals).map((a) => ({
        payment_number: a.payment_number,
        status: a.status,
        actual_amount: a.actual_amount,
        actual_date: a.actual_date,
        late_fee: a.late_fee,
        nsf_fee: a.nsf_fee,
        fee_treatment: a.fee_treatment,
        payment_method: a.payment_method,
        payment_reference: a.payment_reference,
        note: a.note,
      })),
    [actuals]
  );
  // The discipline loop: what the recorded reality means for the loan.
  const projection: Reprojection | null = useMemo(() => {
    if (planned.length === 0) return null;
    return reprojectFromActuals({
      borrowedAmount: planInfo.borrowedAmount,
      annualRate: planInfo.annualRate,
      paymentFrequency: planInfo.paymentFrequency,
      planned,
      actuals: actualRecords,
      asOf: today,
    });
  }, [planInfo, planned, actualRecords, today]);
  // The updated amortization: recorded history, then a re-amortized tail.
  const asTracked = useMemo(() => {
    if (planned.length === 0) return null;
    return buildAsTrackedSchedule({
      borrowedAmount: planInfo.borrowedAmount,
      annualRate: planInfo.annualRate,
      paymentFrequency: planInfo.paymentFrequency,
      planned,
      actuals: actualRecords,
      asOf: today,
    });
  }, [planInfo, planned, actualRecords, today]);
  const feesAssessed = Object.values(actuals).reduce(
    (s, a) => s + (a.late_fee || 0) + (a.nsf_fee || 0),
    0
  );
  const dueSoFar = planned.filter((p) => p.date <= today);
  const plannedToDate = dueSoFar.reduce((s, p) => s + p.amount, 0);
  const paidToDate = dueSoFar.reduce((s, p) => {
    const a = actuals[p.paymentNumber];
    if (!a) return s;
    if (a.status === "skipped") return s;
    return s + (a.actual_amount ?? p.amount);
  }, 0);
  const recordedDue = dueSoFar.filter((p) => actuals[p.paymentNumber]);
  const onTime = recordedDue.filter(
    (p) => actuals[p.paymentNumber]?.status === "paid_as_planned"
  ).length;
  const missed = dueSoFar.filter(
    (p) => actuals[p.paymentNumber]?.status === "skipped"
  ).length;
  const onTimePct = recordedDue.length === 0 ? 0 : Math.round((onTime / recordedDue.length) * 100);

  if (planned.length === 0) {
    return (
      <Card className="p-4 text-sm text-muted-foreground">
        Payment tracking isn't available — this scenario doesn't have a complete repayment schedule yet.
        Open it in the planner to set a term and payment, then come back.
      </Card>
    );
  }

  return (
    <Card className="p-4 space-y-4">
      <div>
        <h3 className="text-base font-semibold">Actual Payment Tracking</h3>
        <p className="text-xs text-muted-foreground mt-1">
          Record real-world repayment against your Family Banking Commitment Agreement™.
          Honest tracking strengthens family banking discipline.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Stat label="Planned to date" value={fmtMoney(plannedToDate)} />
        <Stat label="Paid to date" value={fmtMoney(paidToDate)} />
        <Stat label="On-time" value={`${onTimePct}%`} />
        <Stat label="Missed" value={String(missed)} tone={missed > 0 ? "warn" : undefined} />
        <Stat label="Fees assessed" value={fmtMoney(feesAssessed)} tone={feesAssessed > 0 ? "warn" : undefined} />
      </div>

      {/* Late-charge rule — mirrors the penalty clause in the loan agreement. */}
      <div className="rounded-md border p-3 space-y-3">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold">Late charge rule</h4>
            <p className="text-xs text-muted-foreground">
              Automatically assess a late charge when a payment is recorded past its grace period.
            </p>
          </div>
          <Switch
            checked={rule.enabled}
            onCheckedChange={(v) => saveRule({ ...rule, enabled: v })}
            aria-label="Enable late charge rule"
          />
        </div>
        {rule.enabled && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1">
                <Label className="text-xs">Grace period (days)</Label>
                <Input
                  type="number"
                  min={0}
                  className="h-8 text-sm"
                  value={rule.graceDays}
                  onChange={(e) => setRule({ ...rule, graceDays: Number(e.target.value) || 0 })}
                  onBlur={() => saveRule(rule)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Percent of payment</Label>
                <Input
                  type="number"
                  min={0}
                  step="0.1"
                  className="h-8 text-sm"
                  value={rule.percent}
                  onChange={(e) => setRule({ ...rule, percent: Number(e.target.value) || 0 })}
                  onBlur={() => saveRule(rule)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Minimum charge</Label>
                <CurrencyInput
                  className="h-8 text-sm"
                  value={rule.minimum}
                  formatOnBlur={false}
                  onChange={(v) => setRule({ ...rule, minimum: v ?? 0 })}
                  onBlur={() => saveRule(rule)}
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs">Default fee handling</Label>
                <Select
                  value={rule.capitalizeByDefault ? "capitalized" : "paid_with_payment"}
                  onValueChange={(v) => saveRule({ ...rule, capitalizeByDefault: v === "capitalized" })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid_with_payment">Paid with the payment</SelectItem>
                    <SelectItem value="capitalized">Added to the balance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Charge is the greater of {rule.percent}% of the payment or {fmtMoney(rule.minimum)}, applied when a
              payment is more than {rule.graceDays} day{rule.graceDays === 1 ? "" : "s"} past due. You can override
              the amount on any payment row.
            </p>
          </>
        )}
      </div>

      {projection && (
        <div className="rounded-md border bg-muted/20 p-3 space-y-3">
          <div className="flex items-center gap-2">
            <h4 className="text-sm font-semibold">Where your loan really stands</h4>
            <Badge
              variant="outline"
              className={
                projection.pace === "behind"
                  ? "bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/30"
                  : projection.pace === "ahead"
                    ? "bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30"
                    : "bg-muted text-muted-foreground"
              }
            >
              {projection.pace === "behind" ? "Behind plan" : projection.pace === "ahead" ? "Ahead of plan" : "On track"}
            </Badge>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <Stat label="Balance today" value={fmtMoney(projection.actualBalance)} />
            <Stat
              label="vs plan"
              value={
                projection.pace === "on_track"
                  ? "±$0"
                  : `${projection.balanceVariance > 0 ? "+" : "−"}${fmtMoney(Math.abs(projection.balanceVariance))}`
              }
              tone={projection.pace === "behind" ? "warn" : undefined}
            />
            <Stat
              label="Projected payoff"
              value={projection.projectedPayoffDate ?? "—"}
              tone={!projection.coversInterest ? "warn" : undefined}
            />
            <Stat
              label="Interest impact"
              value={
                !projection.coversInterest
                  ? "—"
                  : projection.projectedExtraInterest > 0.005
                    ? `+${fmtMoney(projection.projectedExtraInterest)}`
                    : projection.projectedExtraInterest < -0.005
                      ? `−${fmtMoney(Math.abs(projection.projectedExtraInterest))} saved`
                      : "$0"
              }
              tone={projection.projectedExtraInterest > 0.005 ? "warn" : undefined}
            />
          </div>
          {!projection.coversInterest && (
            <p className="text-xs text-destructive">
              At the planned payment amount, the balance no longer shrinks — the payment doesn't cover
              period interest. Review the plan in the planner.
            </p>
          )}
          {projection.projectedExtraPayments !== 0 && projection.coversInterest && (
            <p className="text-xs text-muted-foreground">
              {projection.projectedExtraPayments > 0
                ? `At this pace the loan needs ${projection.projectedExtraPayments} extra payment${projection.projectedExtraPayments === 1 ? "" : "s"} beyond the plan (planned payoff ${projection.plannedPayoffDate}).`
                : `At this pace the loan finishes ${Math.abs(projection.projectedExtraPayments)} payment${projection.projectedExtraPayments === -1 ? "" : "s"} early (planned payoff ${projection.plannedPayoffDate}).`}
            </p>
          )}
          {projection.unrecordedDueCount > 0 && (
            <p className="text-xs text-muted-foreground">
              {projection.unrecordedDueCount} due payment{projection.unrecordedDueCount === 1 ? " has" : "s have"} no
              record and {projection.unrecordedDueCount === 1 ? "is" : "are"} assumed paid as planned.
            </p>
          )}
        </div>
      )}

      {loading ? (
        <div className="text-sm text-muted-foreground">Loading payment history…</div>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Due date</TableHead>
                <TableHead className="text-right">Planned</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actual amount</TableHead>
                <TableHead>Actual date</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Cheque # / Ref</TableHead>
                <TableHead className="text-right">Late fee</TableHead>
                <TableHead className="text-right">NSF fee</TableHead>
                <TableHead>Fee handling</TableHead>
                <TableHead>Note</TableHead>
                <TableHead className="w-16"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {planned.slice(0, 240).map((p) => {
                const a = actuals[p.paymentNumber];
                return (
                  <TableRow key={p.paymentNumber}>
                    <TableCell className="text-xs text-muted-foreground">{p.paymentNumber}</TableCell>
                    <TableCell className="whitespace-nowrap text-sm">{p.date}</TableCell>
                    <TableCell className="text-right tabular-nums text-sm">{fmtMoney(p.amount)}</TableCell>
                    <TableCell>
                      <Select
                        value={a?.status ?? ""}
                        onValueChange={(v) =>
                          upsertRow(p, {
                            status: v as ActualStatus,
                            actual_amount:
                              actuals[p.paymentNumber]?.actual_amount ??
                              (v === "skipped" ? 0 : p.amount),
                            actual_date:
                              actuals[p.paymentNumber]?.actual_date ??
                              (v === "skipped" ? null : p.date),
                          })
                        }
                      >
                        <SelectTrigger className="h-8 w-[170px] text-xs">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {(Object.keys(STATUS_LABEL) as ActualStatus[]).map((s) => (
                            <SelectItem key={s} value={s}>
                              {STATUS_LABEL[s]}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {a && (
                        <Badge variant="outline" className={`mt-1 text-[10px] ${STATUS_BADGE[a.status]}`}>
                          {STATUS_LABEL[a.status]}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <CurrencyInput
                        className="h-8 w-28 text-right text-sm"
                        value={a?.actual_amount ?? null}
                        disabled={!a}
                        formatOnBlur={false}
                        onChange={(v) =>
                          a &&
                          upsertRow(p, {
                            status: a.status,
                            actual_amount: v,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Input
                        type="date"
                        className="h-8 w-36 text-sm"
                        value={a?.actual_date ?? ""}
                        disabled={!a}
                        onChange={(e) =>
                          a &&
                          upsertRow(p, {
                            status: a.status,
                            actual_date: e.target.value || null,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={a?.payment_method ?? ""}
                        disabled={!a}
                        onValueChange={(v) =>
                          a && upsertRow(p, { status: a.status, payment_method: v })
                        }
                      >
                        <SelectTrigger className="h-8 w-[130px] text-xs">
                          <SelectValue placeholder="—" />
                        </SelectTrigger>
                        <SelectContent>
                          {PAYMENT_METHODS.map((m) => (
                            <SelectItem key={m} value={m}>
                              {m}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        className="h-8 w-28 text-sm"
                        placeholder="e.g. 1042"
                        value={a?.payment_reference ?? ""}
                        disabled={!a}
                        onChange={(e) =>
                          a &&
                          upsertRow(p, {
                            status: a.status,
                            payment_reference: e.target.value || null,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <CurrencyInput
                        className="h-8 w-24 text-right text-sm"
                        value={a?.late_fee ?? null}
                        disabled={!a}
                        formatOnBlur={false}
                        onChange={(v) => a && upsertRow(p, { status: a.status, late_fee: v ?? 0 })}
                      />
                    </TableCell>
                    <TableCell className="text-right">
                      <CurrencyInput
                        className="h-8 w-24 text-right text-sm"
                        value={a?.nsf_fee ?? null}
                        disabled={!a}
                        formatOnBlur={false}
                        onChange={(v) => a && upsertRow(p, { status: a.status, nsf_fee: v ?? 0 })}
                      />
                    </TableCell>
                    <TableCell>
                      <Select
                        value={a?.fee_treatment ?? "paid_with_payment"}
                        disabled={!a || (a.late_fee || 0) + (a.nsf_fee || 0) === 0}
                        onValueChange={(v) =>
                          a && upsertRow(p, { status: a.status, fee_treatment: v as FeeTreatment })
                        }
                      >
                        <SelectTrigger className="h-8 w-[150px] text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="paid_with_payment">Paid with payment</SelectItem>
                          <SelectItem value="capitalized">Added to balance</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <Input
                        className="h-8 w-40 text-sm"
                        placeholder="Optional"
                        value={a?.note ?? ""}
                        disabled={!a}
                        onChange={(e) =>
                          a &&
                          upsertRow(p, {
                            status: a.status,
                            note: e.target.value || null,
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>
                      {a && (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 px-2 text-xs"
                          onClick={() => clearRow(p)}
                        >
                          Clear
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
          {planned.length > 240 && (
            <p className="text-xs text-muted-foreground mt-2">
              Showing first 240 of {planned.length} scheduled payments.
            </p>
          )}
        </div>
      )}
      {asTracked && (
        <div className="rounded-md border p-3 space-y-3">
          <div className="flex items-center justify-between gap-3">
            <div>
              <h4 className="text-sm font-semibold">Updated Amortization (Reflecting Actuals)</h4>
              <p className="text-xs text-muted-foreground">
                Recorded history followed by an estimated forward schedule re-amortized from today's real balance.
              </p>
            </div>
            <Button size="sm" variant="outline" onClick={() => setShowAsTracked((v) => !v)}>
              {showAsTracked ? "Hide" : "Show"}
            </Button>
          </div>

          {showAsTracked && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <Stat label="Interest to date" value={fmtMoney(asTracked.interestToDate)} />
                <Stat label="Est. remaining interest" value={fmtMoney(asTracked.projectedFutureInterest)} />
                <Stat label="Fees capitalized" value={fmtMoney(asTracked.totalFeesCapitalized)} />
                <Stat
                  label="Est. payoff"
                  value={asTracked.payoffDate ?? "Not on pace"}
                  tone={asTracked.stalled ? "warn" : undefined}
                />
              </div>
              {asTracked.stalled && (
                <p className="text-xs text-destructive">
                  At the current pace the payments do not cover the interest, so this loan never retires. Increase
                  the payment to restore progress.
                </p>
              )}
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Section</TableHead>
                      <TableHead className="text-right">Payment</TableHead>
                      <TableHead className="text-right">Interest</TableHead>
                      <TableHead className="text-right">Principal</TableHead>
                      <TableHead className="text-right">Fees</TableHead>
                      <TableHead className="text-right">Balance</TableHead>
                      <TableHead>Method / Ref</TableHead>
                      <TableHead>Note</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {asTracked.rows.slice(0, 480).map((r) => (
                      <TableRow
                        key={`${r.section}-${r.paymentNumber}`}
                        className={r.section === "projected" ? "text-muted-foreground" : ""}
                      >
                        <TableCell className="text-xs">{r.paymentNumber}</TableCell>
                        <TableCell className="whitespace-nowrap text-sm">{r.date}</TableCell>
                        <TableCell className="text-xs">
                          {r.section === "history" ? (r.assumed ? "Recorded (assumed)" : "Recorded") : "Estimated"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtMoney(r.paymentAmount)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtMoney(r.interest)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtMoney(r.principal)}</TableCell>
                        <TableCell className="text-right tabular-nums text-sm">
                          {r.fees > 0 ? fmtMoney(r.fees) : "—"}
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-sm">{fmtMoney(r.balance)}</TableCell>
                        <TableCell className="text-xs">
                          {[r.method, r.reference].filter(Boolean).join(" ") || "—"}
                        </TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate">{r.note || "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {asTracked.rows.length > 480 && (
                  <p className="text-xs text-muted-foreground mt-2">
                    Showing first 480 of {asTracked.rows.length} rows.
                  </p>
                )}
              </div>
              <p className="text-[11px] text-muted-foreground">
                Estimated figures only. Confirm actual balances and interest with your carrier or lender.
              </p>
            </>
          )}
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: "warn" }) {
  return (
    <div className="rounded-md border bg-muted/30 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className={`text-lg font-semibold tabular-nums ${tone === "warn" ? "text-destructive" : ""}`}>
        {value}
      </div>
    </div>
  );
}